import { useLocation } from "react-router-dom";

import { MENU_ITEMS, MenuItemTypes } from "./menu";
import { buildMenuSections } from "./menuTree";
import { getUserFromCookie } from "../helpers/api/apiCore";
import { usePortalChrome } from "../layouts/PortalLayout";
import type {
  BreadcrumbItem,
  IconName,
  NavItem,
  NavSection,
} from "../layouts/PortalLayout";

// =============================================================================
// Admin portal — PortalLayout nav adapter (Phase 2)
// -----------------------------------------------------------------------------
// Builds the new shell's `NavSection[]` from the SINGLE source of truth
// (`constants/menu.ts`), applying the same permission rule as the legacy Hyper
// menu (an item is visible if it has no `permissions`, or the user holds one of
// them). This keeps the new nav permission-correct and in sync — no second,
// hand-maintained nav to drift.
//
// Mapping: each `isTitle` marker / parent-with-children becomes a NavSection;
// leaf items become NavItems. Legacy Feather icon names are mapped to the
// PortalLayout icon set (with a sensible fallback).
//
// The grouping rules themselves live in `menuTree.ts` (pure, no React imports)
// so the e2e nav suite can import them instead of re-implementing them. This
// file only adds presentation on top.
// =============================================================================

const ICON_MAP: Record<string, IconName> = {
  airplay: "dashboard",
  inbox: "requests",
  "check-square": "offers",
  "credit-card": "invoices",
  repeat: "offers",
  "life-buoy": "support",
  users: "support",
  "user-check": "messages",
  layers: "panel",
  briefcase: "panel",
  file: "documentation",
  navigation: "aircraft",
  grid: "dashboard",
  package: "settings",
  bell: "bell",
  "book-open": "documentation",
  shield: "settings",
  sliders: "settings",
  "message-square": "messages",
};
const DEFAULT_ICON: IconName = "panel";
const mapIcon = (name?: string): IconName => (name && ICON_MAP[name]) || DEFAULT_ICON;

// Per-item icon overrides keyed by the menu item's `key`. Used for sections
// (e.g. Fleet & Inventory) where each leaf should carry an icon matching its
// own name rather than inheriting the parent section's icon.
const ITEM_ICON: Record<string, IconName> = {
  "aviation-aircrafts": "aircraft",
  "aviation-jet-classes": "layers",
  "aviation-airports": "route",
  "aviation-empty-legs": "route",
  "yachts-list": "yacht",
  "yacht-types": "yacht",
  "experiences-list": "yacht",
  cars: "car",
  "event-events": "calendar",
  "event-categories": "tag",
  "waiting-approvals": "documentation",
  "compliance-documents": "offers",
  "compliance-contracts": "edit",
};
const iconFor = (key: string, fallback: IconName): IconName =>
  ITEM_ICON[key] || fallback;

const currentPermissions = (): string[] => {
  const user: any = getUserFromCookie();
  return user && Array.isArray(user.permission) ? user.permission : [];
};

const toNavItem = (item: MenuItemTypes, icon: IconName): NavItem => ({
  key: item.key,
  label: item.label,
  to: item.url || "#",
  icon,
});

/**
 * Build the admin sidebar nav from `MENU_ITEMS`, filtered by the user's
 * permissions. Structure comes from menuTree.buildMenuSections; this only
 * resolves icons — a leaf in a parent-section inherits the parent's icon
 * (section.icon) unless ITEM_ICON overrides it, a leaf under a plain title
 * carries its own.
 */
export const buildAdminNav = (): NavSection[] =>
  buildMenuSections(currentPermissions()).map((section) => ({
    key: section.key,
    title: section.title,
    items: section.items.map((item) =>
      toNavItem(item, iconFor(item.key, mapIcon(section.icon ?? item.icon)))
    ),
  }));

// =============================================================================
// Breadcrumb derivation — "admin / <section> / <page>"
// -----------------------------------------------------------------------------
// Every portal page's breadcrumb is derived from the nav structure rather than
// hand-authored per route. We index every nav leaf to its section title + the
// path it lives under, then resolve the current pathname to the best (longest
// path-prefix) match. Detail pages append their dynamic crumb (e.g. a record
// name) onto this base via withDetailCrumb().
// =============================================================================

type NavIndexEntry = {
  url: string; // prefix used for matching (+ its base)
  base: string;
  section: string;
  label: string;
  to: string; // crumb link target (usually the list page)
};

// Routes whose actual path differs from (or isn't present in) the nav menu, so
// their breadcrumb can still resolve to a proper section. Keeps the nav data
// untouched (nav links keep working); `to` is where the page crumb links.
const NAV_ALIASES: Array<{ match: string; section: string; label: string; to: string }> = [
  // Section labels track the reorganised nav (menu.ts): People→Customers,
  // Vendors & Integrations→Suppliers, Parameters lookups→Reference Data.
  { match: "/management/customers", section: "Customers", label: "Customers", to: "/customers/list" },
  { match: "/event", section: "Fleet & Inventory", label: "Events", to: "/events/list" },
  { match: "/management/offers", section: "Operations", label: "Requests", to: "/management/requests" },
  { match: "/vendor/vendor-apis", section: "Suppliers", label: "Vendor APIs", to: "" },
  { match: "/parameters/infos/info-type-options", section: "Reference Data", label: "Info Type Options", to: "/parameters/infos/info-type-options" },
  { match: "/parameters/infos/infos", section: "Reference Data", label: "Infos", to: "/parameters/infos/infos" },
];

// Build once at module load (permission-independent: a page you're on resolves
// regardless of nav visibility).
const NAV_INDEX: NavIndexEntry[] = (() => {
  const entries: NavIndexEntry[] = [];
  let section: string | null = null;
  const add = (url: string | undefined, sec: string, label: string) => {
    // Skip non-routes and anchor links (e.g. "...?section=x") — they aren't
    // distinct pages and would create false prefix matches.
    if (!url || url === "#" || url.indexOf("?") !== -1) return;
    const base = url.endsWith("/list") ? url.slice(0, -"/list".length) : url;
    entries.push({ url, base, section: sec, label, to: url });
  };
  for (const item of MENU_ITEMS) {
    if (item.isTitle) {
      section = item.label;
      continue;
    }
    if (item.children && item.children.length) {
      // Parent-with-children is its own section (mirrors buildAdminNav).
      for (const c of item.children) add(c.url, item.label, c.label);
      section = null;
      continue;
    }
    add(item.url, section ?? item.label, item.label);
  }
  for (const a of NAV_ALIASES) {
    entries.push({ url: a.match, base: a.match, section: a.section, label: a.label, to: a.to });
  }
  return entries;
})();

const HOME_CRUMB: BreadcrumbItem = { label: "admin", to: "/dashboard" };

// Length of the matched prefix if `candidate` is a path-prefix of `pathname`
// (exact, or a full path segment), else 0.
const prefixScore = (pathname: string, candidate: string): number => {
  if (!candidate || candidate === "/") return 0;
  if (pathname === candidate || pathname.startsWith(candidate + "/")) return candidate.length;
  return 0;
};

/**
 * Derive the "admin / <section> / <page>" breadcrumb for a pathname by matching
 * it against the nav menu (longest path-prefix wins). Falls back to
 * "admin / <fallbackLabel>" when the path isn't represented in the nav.
 */
export const resolveBreadcrumb = (
  pathname: string,
  fallbackLabel?: string
): BreadcrumbItem[] => {
  let best: NavIndexEntry | null = null;
  let bestScore = 0;
  for (const e of NAV_INDEX) {
    const score = Math.max(prefixScore(pathname, e.url), prefixScore(pathname, e.base));
    if (score > bestScore) {
      bestScore = score;
      best = e;
    }
  }
  if (best) {
    const crumbs: BreadcrumbItem[] = [HOME_CRUMB];
    // "Main" is the dashboard's pseudo-section — don't show it as a crumb.
    if (best.section && best.section !== "Main") crumbs.push({ label: best.section });
    crumbs.push(best.to ? { label: best.label, to: best.to } : { label: best.label });
    return crumbs;
  }
  return fallbackLabel ? [HOME_CRUMB, { label: fallbackLabel }] : [HOME_CRUMB];
};

/**
 * Breadcrumb for a detail/sub page: the derived "admin / <section> / <page>"
 * base for `pathname`, with one or more dynamic trailing crumbs appended
 * (e.g. a record name). Use from detail pages via usePortalChrome.
 */
export const withDetailCrumb = (
  pathname: string,
  ...trail: Array<string | BreadcrumbItem>
): BreadcrumbItem[] => [
  ...resolveBreadcrumb(pathname),
  ...trail.map((t) => (typeof t === "string" ? { label: t } : t)),
];

/**
 * Set the topbar breadcrumb for a detail/sub page to the derived
 * "admin / <section> / <page>" base (from the current path) plus the supplied
 * dynamic trailing crumb(s) — e.g. a record name. One-liner replacement for a
 * hand-authored usePortalChrome({ breadcrumb: [...] }) on detail pages.
 */
export const useDetailBreadcrumb = (
  ...trail: Array<string | BreadcrumbItem | false | null | undefined>
): void => {
  const { pathname } = useLocation();
  const clean = trail.filter(
    (t): t is string | BreadcrumbItem => t !== false && t != null
  );
  usePortalChrome({ breadcrumb: withDetailCrumb(pathname, ...clean) });
};
